import { measure } from "@design-system/utils"
import { LayoutEvent } from "./types"
import { useLayoutEffect } from "./useLayoutEffect"

const DOM_LAYOUT_HANDLER_NAME = "__reactLayoutHandler"

let resizeObserver: ResizeObserver | null = null

let didWarn = false

function getResizeObserver(): ResizeObserver | null {
  if (
    typeof window !== "undefined" &&
    typeof window.ResizeObserver !== "undefined"
  ) {
    if (resizeObserver == null) {
      resizeObserver = new window.ResizeObserver((entries) => {
        entries.forEach((entry) => {
          const node = entry.target
          const onLayout = node[DOM_LAYOUT_HANDLER_NAME]
          if (typeof onLayout === "function" && node instanceof HTMLElement) {
            measure(node, (x, y, width, height, left, top) => {
              const event: LayoutEvent = {
                nativeEvent: {
                  layout: { x, y, width, height, left, top },
                  target: node,
                },
                timeStamp: Date.now(),
              }
              Object.defineProperty(event.nativeEvent, "target", {
                enumerable: true,
                get: () => entry.target,
              })
              onLayout(event)
            })
          }
        })
      })
    }
  } else if (!didWarn) {
    if (
      process.env.NODE_ENV !== "production" &&
      process.env.NODE_ENV !== "test"
    ) {
      console.warn(
        "onLayout relies on ResizeObserver which is not supported by your browser. " +
          "Please include a polyfill, e.g., https://github.com/que-etc/resize-observer-polyfill.",
      )
      didWarn = true
    }
  }

  return resizeObserver
}

export function useElementLayout<T extends Element>(
  ref: React.RefObject<T>,
  onLayout?: (e: LayoutEvent) => void,
) {
  const observer = getResizeObserver()

  useLayoutEffect(() => {
    const node = ref.current
    if (node != null) {
      node[DOM_LAYOUT_HANDLER_NAME] = onLayout
    }
  }, [ref, onLayout])

  // Observing is done in a separate effect to avoid this effect running
  // when 'onLayout' changes.
  useLayoutEffect(() => {
    const node = ref.current
    if (node != null && observer != null) {
      if (typeof node[DOM_LAYOUT_HANDLER_NAME] === "function") {
        observer.observe(node)
      } else {
        observer.unobserve(node)
      }
    }
    return () => {
      if (node != null && observer != null) {
        observer.unobserve(node)
      }
    }
  }, [ref, observer])
}
